import { Button, Dialog, DialogContent, Grid, Tab, Tabs, TextField } from '@mui/material';
import React from 'react';

/**
 * SearchPopup Component
 * 
 * A popup dialog that allows users to search based on different criteria using tabs.
 * Uses MUI components such as Dialog, Tabs, TextField, and Button.
 *
 * @component
 * @memberof Components
 * @param {Object} props - The properties passed to the component.
 * @param {boolean} props.searchBarrPopupOpen - Controls the visibility of the popup.
 * @param {Function} props.handleCloseSearchBarPopup - Callback function to close the popup.
 * @param {number} props.selectedTab - The currently selected tab index.
 * @param {Function} props.handleTabChange - Function to handle tab selection change.
 * @param {string[]} props.tabLabels - An array of tab labels.
 * @param {Object} props.searchFormik - Formik object for form handling, including values, errors, and handlers.
 *
 * @example
 * <SearchPopup
 *   searchBarrPopupOpen={isOpen}
 *   handleCloseSearchBarPopup={handleClose}
 *   selectedTab={selectedTab}
 *   handleTabChange={handleChange}
 *   tabLabels={["Reference No", "Application ID", "CNIC", "Name"]}
 *   searchFormik={formik}
 * />
 *
 * @returns {JSX.Element} A MUI Dialog containing search fields and tabs.
 */

const SearchPopup = ({ searchBarrPopupOpen, handleCloseSearchBarPopup, selectedTab, handleTabChange, tabLabels, searchFormik }) => {
    return (
        < Dialog
            open={searchBarrPopupOpen}
            onClose={handleCloseSearchBarPopup}
            maxWidth="sm"
            fullWidth
            sx={{
                position: 'fixed',
                top: '-67%',
                left: '56%',
                '& .MuiDialog-paper': {
                    borderRadius: '12px',
                },
            }}
        >
            <DialogContent sx={{ background: 'linear-gradient(to bottom, white, #D8E8FF, white)' }}>
                <Tabs
                    value={selectedTab}
                    onChange={handleTabChange}
                    textColor="inherit"
                    sx={{
                        '& .MuiTabs-indicator': {
                            display: 'none',
                        },
                        color: 'white',
                    }}
                >
                    {tabLabels.map((label, index) => (
                        <Tab
                            key={index}
                            label={label}
                            sx={{
                                backgroundColor: selectedTab == index ? '#006ad1' : '#F7F9FB',
                                color: selectedTab == index ? '#F7F9FB' : '#AEAEAE',
                                width: 'auto',
                                marginLeft: index != 0 ? '5px' : '0px',
                                marginRight: index != tabLabels.length - 1 ? '5px' : '0px',
                                minWidth: '120px',
                                border: selectedTab == index ? '' : '2px solid #989a9c',
                                borderRadius: '6px',
                                fontSize: '13px',
                            }}
                        />
                    ))}
                </Tabs>
                <Grid container sx={{ display: 'flex', flexDirection: 'row', gap: '10px', alignItems: 'center' }} >
                    <Grid item lg={10} >
                        {selectedTab == 0 && (
                            <>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    margin="dense"
                                    name='iv_reference_no'
                                    id='iv_reference_no'
                                    placeholder="Enter Reference No."
                                    value={searchFormik.values.iv_reference_no}
                                    onChange={searchFormik.handleChange}
                                    onBlur={searchFormik.handleBlur}
                                    inputProps={{ maxLength: 14 }}
                                    sx={{
                                        backgroundColor: 'white',

                                        '& .MuiOutlinedInput-root': {
                                            padding: '0px 14px',
                                            '& fieldset': {
                                                borderColor: '#ccc9c0',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: '#aaa',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#ccc9c0',
                                            },
                                            '& input': {
                                                padding: '10px 0px',
                                            },
                                        },
                                    }}
                                />
                                <>
                                    {searchFormik.touched.iv_reference_no && searchFormik.errors.iv_reference_no && (
                                        <p style={{ color: 'red' }}>{searchFormik.errors.iv_reference_no}</p>
                                    )}
                                </>
                            </>
                        )}
                        {selectedTab == 1 && (
                            <>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    margin="dense"
                                    placeholder="Enter Application ID"
                                    name='iv_application_id'
                                    id='iv_application_id'
                                    value={searchFormik.values.iv_application_id}
                                    onChange={searchFormik.handleChange}
                                    onBlur={searchFormik.handleBlur}
                                    inputProps={{ maxLength: 19, minLength: 17 }}
                                    sx={{
                                        backgroundColor: 'white',
                                        '& .MuiOutlinedInput-root': {
                                            padding: '0px 14px',
                                            '& fieldset': {
                                                borderColor: '#ccc9c0',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: '#aaa',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#ccc9c0',
                                            },
                                            '& input': {
                                                padding: '10px 0px',
                                            },
                                        },
                                    }}
                                />
                                <>
                                    {searchFormik.touched.iv_application_id && searchFormik.errors.iv_application_id && (
                                        <p style={{ color: 'red' }}>{searchFormik.errors.iv_application_id}</p>
                                    )}
                                </>
                            </>
                        )}
                        {selectedTab == 2 && (
                            <>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    margin="dense"
                                    placeholder="Enter CNIC"
                                    name='iv_cnic'
                                    id='iv_cnic'
                                    value={searchFormik.values.iv_cnic}
                                    onChange={searchFormik.handleChange}
                                    onBlur={searchFormik.handleBlur}
                                    inputProps={{ maxLength: 13 }}
                                    sx={{
                                        backgroundColor: 'white',
                                        '& .MuiOutlinedInput-root': {
                                            padding: '0px 14px',
                                            '& fieldset': {
                                                borderColor: '#ccc9c0',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: '#aaa',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#ccc9c0',
                                            },
                                            '& input': {
                                                padding: '10px 0px',
                                            },
                                        },
                                    }}
                                />
                                <>
                                    {searchFormik.touched.iv_cnic && searchFormik.errors.iv_cnic && (
                                        <p style={{ color: 'red' }}>{searchFormik.errors.iv_cnic}</p>
                                    )}
                                </>
                            </>
                        )}
                        {selectedTab == 3 && (
                            <>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    margin="dense"
                                    placeholder="Enter Name"
                                    name='iv_applicant_name'
                                    id='iv_applicant_name'
                                    value={searchFormik.values.iv_applicant_name}
                                    onChange={searchFormik.handleChange}
                                    onBlur={searchFormik.handleBlur}
                                    inputProps={{ minLength: 3 }}
                                    sx={{
                                        backgroundColor: 'white',
                                        '& .MuiOutlinedInput-root': {
                                            padding: '0px 14px',
                                            '& fieldset': {
                                                borderColor: '#ccc9c0',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: '#aaa',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#ccc9c0',
                                            },
                                            '& input': {
                                                padding: '10px 0px',
                                            },
                                        },
                                    }}
                                />
                                <>
                                    {searchFormik.touched.iv_applicant_name && searchFormik.errors.iv_applicant_name && (
                                        <p style={{ color: 'red' }}>{searchFormik.errors.iv_applicant_name}</p>
                                    )}
                                </>
                            </>
                        )}
                    </Grid>
                    <Grid item lg={1} >
                        <Button
                            fullWidth
                            onClick={searchFormik.handleSubmit}
                            color="primary"
                            variant="contained"
                            sx={{
                                background: '#006ad1',
                                width: '17px',
                                padding: '10px',
                                height: '40px',
                            }}
                        >
                            Go
                        </Button>
                    </Grid>
                </Grid>
                {/* <p style={{ color: 'red', textAlign: 'center' }}>Search scope is limited to past 60 days</p> */}
            </DialogContent>
        </Dialog >
    )
}

export default SearchPopup